import type { PbxSettingsLocale } from './en'

export const pbxSettingsFrCa = {
  loading: 'Chargement des paramètres PBX...',
  invalidPbx: {
    title: 'PBX invalide',
    description:
      'Un identifiant PBX valide est requis pour modifier les paramètres PBX. Veuillez sélectionner un PBX dans le tableau de bord.',
  },
  loadError: {
    title: 'Impossible de charger les paramètres PBX',
    description: 'Veuillez réessayer.',
  },
  partialLoadWarning: {
    title: 'Certaines données n’ont pas pu être chargées',
    unknownError: 'Erreur inconnue',
    labels: {
      providerGateways: 'Passerelles fournisseur',
      serverGroups: 'Groupes de serveurs',
      phoneNumbers: 'Numéros de téléphone',
      virtualFaxes: 'Faxes virtuels',
      alert911Options: 'Options 911',
    },
  },
  actions: {
    update: 'Mettre à jour',
  },
  common: {
    loading: 'Chargement...',
    select: 'Sélectionner',
  },
  sections: {
    core: {
      title: 'Paramètres PBX',
    },
    advanced: {
      title: 'Paramètres PBX avancés',
    },
    system: {
      title: 'Paramètres système',
    },
  },
  placeholders: {
    selectFromList: 'Sélectionner dans la liste...',
    selectServerGroup: 'Sélectionner un groupe de serveurs',
    selectTimeZone: 'Sélectionner un fuseau horaire',
    selectExtensionDigits: 'Sélectionner les chiffres d’extension',
    selectCodec: 'Sélectionner un codec',
  },
  fields: {
    pbxDisplayName: {
      label: 'Nom d’affichage PBX',
    },
    company: {
      label: 'Entreprise',
    },
    providerGateway: {
      label: 'Passerelle fournisseur',
    },
    emergencyProviderGateway: {
      label: 'Passerelle fournisseur d’urgence',
    },
    internationalProviderGateway: {
      label: 'Passerelle fournisseur internationale',
    },
    active: {
      label: 'Actif',
    },
    smsSenderName: {
      label: 'Nom de l’expéditeur SMS',
      copyFromCompany: 'Copier de la compagnie',
      helperText:
        'Montré à vos clients dans les réponses de désabonnement SMS, p. ex. « {{name}} : Vous vous êtes désabonné avec succès. » Ceci est par emplacement — modifiez-le ici s’il diffère du nom de la compagnie.',
      divergenceHint:
        'Diffère du nom de la compagnie « {{companyName}} » — c’est normal pour les clients multi-emplacements.',
    },
    serverGroup: {
      label: 'Groupe de serveurs',
    },
    advanced: {
      maximum_allowed_channels: 'Canaux max',
      maximum_lines_in: 'Canaux entrants',
      maximum_lines_out: 'Canaux par extension',
      call_log_retention_period_in_months: 'Historique des appels (en mois)',
    },
    industry: {
      label: 'Secteur',
    },
    ehr: {
      label: 'DSE',
      helperText:
        'À des fins de rapport seulement, n’affecte pas la fonctionnalité',
    },
    defaultEmergencyAlert: {
      label: 'Alerte d’urgence par défaut',
    },
    defaultTimeZone: {
      label: 'Fuseau horaire par défaut',
      britishColumbia: 'Heure du Pacifique - Colombie-Britannique (PCT)',
    },
    callParkTimeout: {
      label: 'Délai d’attente du parcage d’appel',
    },
    extensionDigits: {
      label: 'Chiffres d’extension',
    },
    defaultAreaCode: {
      label: 'Indicatif régional par défaut',
    },
    defaultCallerId: {
      label: 'Affichage appelant par défaut',
    },
    defaultFaxNumber: {
      label: 'Numéro de fax par défaut',
    },
    defaultVirtualFax: {
      label: 'Fax virtuel par défaut',
    },
    preferredCodec: {
      label: 'Codec préféré',
    },
    checkboxes: {
      enable_inbound_recording: 'Enregistrement des appels entrants',
      enable_outbound_recording: 'Enregistrement des appels sortants',
      enable_internal_recording: 'Enregistrement des appels internes',
      international: 'International',
      enable_vm_transcription: 'Transcription de messagerie vocale',
      schedules_v2: 'Horaire V2',
      enable_agent_statuses: 'Centre d’appels',
      enable_call_analytics: 'Mango AI',
      is_grove: 'Grove',
    },
  },
  companySelect: {
    placeholder: 'Sélectionner une entreprise',
    ariaLabel: 'Sélecteur d’entreprise',
    searchPlaceholder: 'Rechercher des entreprises...',
    empty: {
      loading: 'Chargement des entreprises...',
      noResults: 'Aucune entreprise trouvée',
      typeToSearch: 'Saisissez pour rechercher des entreprises',
    },
  },
  phoneSelect: {
    placeholder: 'Sélectionner dans la liste...',
    ariaLabel: 'Sélecteur de numéro de téléphone',
    searchPlaceholder: 'Rechercher des numéros de téléphone...',
    empty: {
      loading: 'Chargement des numéros de téléphone...',
      noResults: 'Aucun numéro de téléphone trouvé',
      noData: 'Aucun numéro de téléphone disponible',
    },
  },
  toast: {
    validationError: 'Erreur de validation',
    timezoneChanged: {
      title: 'Fuseau horaire modifié',
      description:
        'Vous avez modifié le fuseau horaire PBX. N’oubliez pas de mettre à jour vos horaires en conséquence.',
    },
    noChanges: 'Aucun changement à enregistrer',
    saveSuccess: 'Paramètres PBX mis à jour',
    saveError: {
      title: 'Échec de la mise à jour',
      description:
        'Un problème est survenu lors de la mise à jour des paramètres PBX.',
    },
  },
  callParkConfirm: {
    title: 'Confirmer le délai de parcage d’appel',
    description:
      'Voulez-vous vraiment garder les appelants dans le parcage pendant plus de 10 minutes?',
    cancel: 'Réinitialiser le délai',
    confirm: 'Continuer',
  },
  rebuildAndReboot: {
    title: 'Paramètres PBX mis à jour',
    description:
      'Certains paramètres PBX peuvent ne pas prendre effet sur les appareils tant qu’ils n’ont pas été reconstruits et redémarrés.',
    cancel: 'Pas maintenant',
    confirm: 'Reconstruire et redémarrer les appareils',
    toast: {
      successTitle: 'Reconstruction et redémarrage démarrés',
      successDescription:
        'Tous les appareils sont en cours de reconstruction et de redémarrage.',
      errorTitle: 'Échec de la reconstruction et du redémarrage',
      errorDescription:
        'Un problème est survenu lors de la reconstruction et du redémarrage des appareils.',
    },
  },
  validation: {
    pbxDisplayNameRequired: 'Veuillez saisir un Nom d’affichage PBX.',
    companyRequired: 'Veuillez sélectionner une Entreprise.',
    smsSenderNameRequired: 'Veuillez saisir un Nom de l’expéditeur SMS.',
    serverGroupRequired: 'Veuillez sélectionner un Groupe de serveurs.',
    primaryOutboundGatewayRequired:
      'Veuillez sélectionner une Passerelle de sortie principale.',
    primaryInternationalGatewayRequired:
      'Veuillez sélectionner une Passerelle internationale principale.',
    emergencyGatewayRequired: 'Veuillez sélectionner une Passerelle d’urgence.',
    industryRequired: 'Veuillez sélectionner un Secteur.',
    maxChannelsRequired: 'Veuillez saisir les Canaux max.',
    inboundChannelsRequired: 'Veuillez saisir les Canaux entrants.',
    perExtensionChannelsRequired: 'Veuillez saisir les Canaux par extension.',
    callHistoryRequired: 'Veuillez saisir l’Historique des appels.',
    numericFieldRange: '{{field}} doit être entre {{min}} et {{max}}.',
    numericFieldMin: 'Le minimum est {{min}}.',
    numericFieldMax: 'Le maximum est {{max}}.',
    defaultTimeZoneRequired:
      'Veuillez sélectionner un Fuseau horaire par défaut.',
    callParkTimeoutRange:
      'Veuillez saisir un Délai de parcage d’appel entre {{min}}-{{max}} (Recommandé : {{recommended}}).',
    callParkTimeoutMax:
      'Le Délai de parcage d’appel ne peut pas dépasser {{max}} secondes.',
    extensionDigitsRequired: 'Veuillez saisir les Chiffres d’extension.',
    extensionDigitsInvalid: 'Les Chiffres d’extension doivent être 3, 4 ou 5.',
    defaultAreaCodeRequired: 'Veuillez saisir l’Indicatif régional par défaut.',
    defaultAreaCodeDigits:
      'L’Indicatif régional par défaut doit comporter 3 chiffres.',
    defaultAreaCodeRange:
      'L’Indicatif régional par défaut doit être entre 200 et 998.',
    defaultAreaCodeDisallowed:
      'L’indicatif régional {{code}} n’est pas autorisé pour l’Indicatif régional par défaut.',
    defaultCallerIdRequired:
      'Veuillez sélectionner un Affichage appelant par défaut.',
    defaultFaxNumberRequired:
      'Veuillez sélectionner un Numéro de fax par défaut.',
    defaultVirtualFaxRequired:
      'Veuillez sélectionner un Fax virtuel par défaut.',
    preferredCodecRequired: 'Veuillez sélectionner un Codec préféré.',
  },
} satisfies PbxSettingsLocale
